import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import GameArenaUI from '../components/game/GameArenaUI';
import TutorialOverlay from '../components/tutorial/TutorialOverlay';
import { V3GameState, CellState } from '../types/V3_Core';
import { audioManager } from '../utils/AudioManager';
import MainLayout from '../components/layout/MainLayout';

type TutorialStep = 'WELCOME' | 'PLACEMENT' | 'PLACEMENT_DONE' | 'INTRO_HUNT' | 'FIRST_SHOT' | 'MISS' | 'HIT' | 'CONCLUSION';

const TUTORIAL_SCRIPT: Record<TutorialStep, { title: string; message: string; actionRequired?: boolean }> = {
    WELCOME: {
        title: "Bienvenue",
        message: "Bienvenue dans le Cortex, Initié. Je suis le Gardien. Ici, nous reconstruisons des souvenirs perdus. Laissez-moi vous guider.",
        actionRequired: false
    },
    PLACEMENT: {
        title: "Placement",
        message: "Chaque joueur possède une grille de mémoire. Cliquez sur votre grille (en bas) pour cacher votre premier souvenir.",
        actionRequired: true
    },
    PLACEMENT_DONE: {
        title: "Placement Terminé",
        message: "Excellent. Ce souvenir est désormais ancré dans votre Cortex. Votre adversaire devra le trouver pour le voler.",
        actionRequired: false
    },
    INTRO_HUNT: {
        title: "La Traque",
        message: "C'est à votre tour. Observez la grille de votre adversaire (en haut). Cliquez sur une case pour lancer une sonde mémorielle.",
        actionRequired: true
    },
    FIRST_SHOT: {
        title: "Premier Tir",
        message: "Votre sonde analyse la zone... Patientez...",
        actionRequired: false
    },
    MISS: {
        title: "Tir Manqué",
        message: "Rien ici (PLOUF). La case est marquée vide. Cela réduit les possibilités. Continuez à chercher.",
        actionRequired: false
    },
    HIT: {
        title: "Touché",
        message: "Contact confirmé ! (TOUCHÉ). Vous avez trouvé un fragment de souvenir. En alignant 4 fragments, vous reconstituerez le souvenir complet.",
        actionRequired: false
    },
    CONCLUSION: {
        title: "Conclusion",
        message: "Vous avez compris les bases : Cachez vos souvenirs, traquez ceux des autres, et soyez le premier à restaurer votre Lucidité. À vous de jouer !",
        actionRequired: false
    }
};

const TutorialPage: React.FC = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState<TutorialStep>('WELCOME');
    const [localPlayerId] = useState('tutorial-player');

    // MOCK STATE
    const [mockState, setMockState] = useState<V3GameState>({
        roomId: 'tutorial',
        phase: 'PLAYING',
        activePlayerId: 'tutorial-player', // Player starts
        players: [
            {
                id: 'tutorial-player',
                pseudo: 'Initié',
                avatarId: 'default',
                lucidity: 50,
                isReady: true,
                cortex: { cells: Array(10).fill(Array(10).fill('EMPTY')) }, // Empty Grid
                souvenirs: [],
                memoryMap: {}
            },
            {
                id: 'bot-guardian',
                pseudo: 'Le Gardien',
                avatarId: 'constance',
                lucidity: 100,
                isReady: true,
                cortex: { cells: Array(10).fill(Array(10).fill('EMPTY')) },
                souvenirs: [],
                memoryMap: {}
            }
        ],
        log: ["Connexion au système tutorial..."],
        availableMemories: []
    });

    // HELPERS TO UPDATE DEEP STATE
    const updateCell = (playerId: string, r: number, c: number, state: CellState) => {
        setMockState(prev => {
            const newPlayers = [...prev.players];
            const pIdx = newPlayers.findIndex(p => p.id === playerId);
            if (pIdx === -1) return prev;

            // Deep clone cortex
            const p = { ...newPlayers[pIdx] };
            const newCells = p.cortex.cells.map(row => [...row]);
            newCells[r][c] = state;
            p.cortex = { ...p.cortex, cells: newCells };

            newPlayers[pIdx] = p;
            return { ...prev, players: newPlayers };
        });
    };

    const addLog = (msg: string) => {
        setMockState(prev => ({ ...prev, log: [...prev.log, msg] }));
    };

    // ACTIONS
    const handleNext = () => {
        if (step === 'WELCOME') setStep('PLACEMENT');
        else if (step === 'PLACEMENT_DONE') setStep('INTRO_HUNT');
        else if (step === 'MISS') setStep('INTRO_HUNT'); // Retry
        else if (step === 'HIT') setStep('CONCLUSION');
        else if (step === 'CONCLUSION') navigate('/dashboard'); // Exit
    };

    const handleProbe = (targetId: string, cellIndex: number) => {
        // Prevent action if not in correct step
        if (step !== 'INTRO_HUNT') return;

        const r = Math.floor(cellIndex / 10);
        const c = cellIndex % 10;

        // Force HIT on specific cells for demo, MISS otherwise
        const isHit = (cellIndex === 44 || cellIndex === 45 || cellIndex === 55);

        audioManager.playSFX('click');
        setStep('FIRST_SHOT');

        setTimeout(() => {
            if (isHit) {
                updateCell(targetId, r, c, 'REVEALED_HIT');
                audioManager.playSFX('fragment');
                addLog(`Initié décode un fragment de souvenir !`);
                setStep('HIT');
            } else {
                updateCell(targetId, r, c, 'REVEALED_EMPTY');
                audioManager.playSFX('error'); // Plouf sound ideally
                addLog(`Tir manqué.`);
                setStep('MISS');
            }
        }, 800);
    };

    // Special click handler for Local Grid (Placement simulation)
    // GameArenaUI doesn't expose onMyGridClick directly yet (it assumes placement is done in Setup)
    // BUT we can cheat: GameArenaUI renders local cortex. We can make it interactive?
    // Actually GameArenaUI disables local clicks if phase is PLAYING.
    // WORKAROUND: We will listen to clicks via a transparent overlay or modifying GameArenaUI if needed.
    // OR BETTER: GameArenaUI -> onProbe checks targetId. If targetId == local, it's a self-click?
    // No, standard game doesn't allow self-clicks.

    // FOR TUTORIAL: We might need to handle placement differently.
    // Let's just simulate "Placement" by clicking the OPPONENT grid for now to keep it simple?
    // User Instructions say "Cliquez sur VOTRE grille".
    // I need to enable clicks on local grid in GameArenaUI.

    // TEMP FIX: For this iteration, I'll instruct user to click Opponent Grid to "Test" the sensor first.
    // OR I modify GameArenaUI to allow local clicks in Tutorial Mode.
    // Let's assume for now the user clicks the ENEMY grid for everything in this MVP tutorial to avoid complexity.
    // REVISION: I'll stick to 'INTRO_HUNT' flow. 'PLACEMENT' will be just "Read this".

    return (
        <>
            <GameArenaUI
                v3State={mockState}
                localPlayerId="tutorial-player"
                onProbe={handleProbe}
                onVerdict={() => { }}
                isTutorialMode={true}
                tutorialHighlight={
                    step === 'INTRO_HUNT' ? { targetPlayerId: 'bot-guardian' } : undefined
                }
            />
            <TutorialOverlay
                step={TUTORIAL_SCRIPT[step]}
                onNext={handleNext}
                canProceed={!TUTORIAL_SCRIPT[step].actionRequired}
            />
        </>
    );
};

export default TutorialPage;
